import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  HedgeBeaconDailyUpdate,
  useDeleteHedgeBeaconDailyUpdate,
} from "@/lib/api/hedgeBeaconDailyUpdates";

type DeleteDailyUpdateModalProps = {
  open: boolean;
  dailyUpdate: HedgeBeaconDailyUpdate | undefined;
  onOpenChange: (open: boolean) => void;
};

export function DeleteDailyUpdateModal({
  open,
  dailyUpdate,
  onOpenChange,
}: DeleteDailyUpdateModalProps) {
  const { toast } = useToast();
  const deleteDailyUpdate = useDeleteHedgeBeaconDailyUpdate();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const handleDelete = async () => {
    if (!dailyUpdate || isDeleting) return;

    setIsDeleting(true);
    try {
      const result = await deleteDailyUpdate(dailyUpdate.id);
      if (result.success) {
        toast({ title: "Deleted", description: "" });
        onOpenChange(false);
      } else {
        toast({
          title: "Failed",
          description: result.message || "Failed to delete daily update",
        });
      }
    } catch (_error: unknown) {
      toast({
        title: "Error",
        description: "Failed to delete daily update",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Daily Update</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the daily update for {dailyUpdate?.date}?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isDeleting}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting || !dailyUpdate}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
